import React, { useEffect, useState, useRef } from "react";

import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  LinearProgress,
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import StepperView from "./components/Stepper";
import ButtonView from "../common/Button";
import Upload from "./components/Upload";
import { StepsEnum } from "./types";
import ProjectAndDetails from "./components/ProjectAndDetails";
import Summary from "./components/Summary";
import { createEspisodeService } from "./services";
import PricingPlaneDialog from "../common/PricingPlansDialog";
import Headerbar from "../common/Headerbar";

interface UploadPayload {
  formData: Record<string, any>;
  file: File;
}

export default function CreateEpisode() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState<string>("");
  const [nameError, setNameError] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<{
    label: string;
    id: string;
  } | null>(null);
  const [isPlanLimitReached, setIsPlanLimitReached] = useState<boolean>(false);
  const [openPricingPlanDialog, setOpenPricingPlanDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [projectSelectionError, setProjectSelectionError] =
    useState<boolean>(false);
  const [fileDurationInSeconds, setFileDurationInSeconds] = useState<number>(0);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(0);

  const handleOpenPricingPlanDialog = () => {
    setOpenPricingPlanDialog(!openPricingPlanDialog);
  };

  const onUploadProgress = (progress: ProgressEvent | any) => {
    if (progress.totalProgress !== undefined) {
      setUploadProgress(progress.totalProgress);
    } else {
      const total = progress.total || 1;
      const current = progress.loaded;
      const percentCompleted = Math.round((current / total) * 100);
      setUploadProgress(percentCompleted);
    }
  };

  const uploadMutation = useMutation({
    mutationKey: ["upload"],
    mutationFn: ({ formData, file }: UploadPayload) => {
      return createEspisodeService.uploadFile(file, formData, onUploadProgress);
    },
    onSuccess: () => {
      setRedirectCountdown(10);
      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate("/episodes");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onError: (error: Record<string, any>) => {
      const statusCode = error?.statusCode || error?.response?.status;
      const errorMessage =
        error?.error ||
        error?.message ||
        error?.response?.data?.message ||
        "Upload failed";

      if (statusCode === 429) {
        setIsPlanLimitReached(true);
        toast.error(`Insufficient credits: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        toast.error(`Upload failed: ${errorMessage}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    },
  });

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (files?.length > 0 && !uploadMutation.isSuccess) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [files, uploadMutation.isSuccess]);

  const handleUpload = () => {
    if (!selectedProject?.id) {
      setProjectSelectionError(true);
      return;
    }
    if (!name.trim()) {
      setNameError(true);
      return;
    }

    setProjectSelectionError(false);
    setNameError(false);
    const formData = { name, project_id: selectedProject.id };

    uploadMutation.mutate({ formData, file: files[0] });
  };

  const handleNextStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePreviousStep = () => {
    if (activeStep === StepsEnum.SUMMARY) {
      setIsPlanLimitReached(false);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleEpisodeNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setName(event.target.value);
    if (!event.target.value) {
      setNameError(true);
    } else {
      setNameError(false);
    }
  };

  const handleProject = (
    _event: React.SyntheticEvent,
    data: { label: string; id: string },
  ) => {
    setSelectedProject(data);
    setProjectSelectionError(false);
  };

  // Update file duration when files change
  useEffect(() => {
    if (files.length > 0) {
      const file = files[0] as any;
      if (file.duration_in_seconds) {
        setFileDurationInSeconds(file.duration_in_seconds);
      }
    }
  }, [files]);

  const sidebarRef = useRef<SidebarRef>(null);
  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const isNextDisabled = () => {
    if (activeStep === StepsEnum.UPLOAD) {
      return files.length === 0;
    }
    if (activeStep === StepsEnum.PROJECT_AND_DETAILS) {
      return !selectedProject?.id || !name.trim();
    }
    return false;
  };

  return (
    <>
      <SidebarView ref={sidebarRef} pageName="Upload Episode" />
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Headerbar
          pageName="Upload Episode"
          onMobileMenuClick={handleMobileMenuToggle}
        />
        <Card
          component={Stack}
          spacing={0}
          direction="column"
          sx={{
            position: "relative",
            px: 4,
            py: 4,
            borderRadius: 0,
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
            margin: 0,
            marginLeft: 6,
            marginRight: 6,
          }}
        >
          {/* Modern stepper container */}
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 4 },
              borderRadius: 4,
              boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
              mb: 4,
            }}
          >
            <StepperView
              activeStep={activeStep}
              isUploadComplete={uploadProgress === 100}
            />
          </Paper>

          {/* Content container */}
          {!uploadMutation.isPending && !uploadMutation.isSuccess && (
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 4 },
                borderRadius: 4,
                boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                mb: 4,
                minHeight: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 600 }}>
                {activeStep === StepsEnum.UPLOAD && (
                  <Upload setFiles={setFiles} files={files} />
                )}
                {activeStep === StepsEnum.PROJECT_AND_DETAILS && (
                  <ProjectAndDetails
                    name={name}
                    nameError={nameError}
                    handleEpisodeNameChange={handleEpisodeNameChange}
                    selectedProject={selectedProject}
                    handleProject={handleProject}
                    projectSelectionError={projectSelectionError}
                  />
                )}
                {activeStep === StepsEnum.SUMMARY && (
                  <Summary
                    files={files}
                    fileDurationInSeconds={fileDurationInSeconds}
                    name={name}
                    selectedProject={selectedProject}
                    isPlanLimitReached={isPlanLimitReached}
                    handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Upload progress */}
          {uploadMutation.isPending && (
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 4 },
                borderRadius: 4,
                boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                mb: 4,
              }}
            >
              <Box sx={{ width: "100%", maxWidth: 600, mx: "auto" }}>
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                  Uploading Episode...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#f0f0f0",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                    },
                  }}
                />
                <Box sx={{ textAlign: "center", mt: 2, fontWeight: 600 }}>
                  {uploadProgress}%
                </Box>
              </Box>
            </Paper>
          )}

          {/* Success message with countdown */}
          {uploadMutation.isSuccess && redirectCountdown > 0 && (
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 4 },
                borderRadius: 4,
                boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
                mb: 4,
                background: "linear-gradient(135deg, #4ade80, #22c55e)",
                color: "white",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 600,
                  mx: "auto",
                  textAlign: "center",
                }}
              >
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  🎉 Upload Successful!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                  Your episode has been uploaded successfully and is being
                  processed.
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Redirecting to episodes page in {redirectCountdown} seconds...
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Navigation buttons */}
          {!uploadMutation.isPending && !uploadMutation.isSuccess && (
            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  {activeStep > 0 && (
                    <ButtonView
                      variant="outlined"
                      label="Previous"
                      type="button"
                      handleOnClick={handlePreviousStep}
                      disabled={
                        uploadMutation.isPending || uploadMutation.isSuccess
                      }
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        fontSize: 15,
                        textTransform: "none",
                        fontWeight: 600,
                        letterSpacing: 0.2,
                      }}
                    />
                  )}
                </Box>
                <Box>
                  {activeStep === StepsEnum.SUMMARY ? (
                    <ButtonView
                      variant="contained"
                      label="Upload Episode"
                      type="button"
                      handleOnClick={handleUpload}
                      disabled={
                        !selectedProject?.id || !name.trim() || !files.length
                      }
                      startIcon={<CloudUploadIcon />}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        fontSize: 15,
                        textTransform: "none",
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    />
                  ) : (
                    <ButtonView
                      variant="contained"
                      label="Next"
                      type="button"
                      handleOnClick={handleNextStep}
                      disabled={isNextDisabled()}
                      sx={{
                        borderRadius: 999,
                        px: 3,
                        py: 1,
                        fontSize: 15,
                        textTransform: "none",
                        fontWeight: 600,
                        letterSpacing: 0.2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    />
                  )}
                </Box>
              </Stack>
            </Paper>
          )}
        </Card>
      </Box>
      <PricingPlaneDialog
        handleOpenPricingPlanDialog={handleOpenPricingPlanDialog}
        open={openPricingPlanDialog}
      />
    </>
  );
}
